import { TokenIcon } from "@/components/brand/TokenIcon";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import { AutonomyStatusPanel } from "@/components/oracle/AutonomyStatusPanel";
import { ExternalProvidersSection } from "@/components/oracle/ExternalProvidersSection";
import { PriceCallHistoryPanel } from "@/components/oracle/PriceCallHistoryPanel";
import { Button } from "@/components/ui/Button";
import type { CollateralKey } from "@/data/fixtures";
import { useOracleTransparency } from "@/hooks/useOracleTransparency";
import {
	isLocalOracle,
	isPlayOrLocalOracle,
	isProductionOracle,
} from "@/lib/oracle/mode";
import {
	formatCooldown,
	manualRefreshRemainingMs,
	readLastManualRefreshAt,
	writeLastManualRefreshAt,
} from "@/lib/oracle/refresh-policy";
import { ROUTES } from "@/lib/routes";
import { formatNum } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const ASSETS: CollateralKey[] = ["ICP", "ckBTC", "ckETH"];
const XRC_MAINNET = "uf6dk-hyaaa-aaaaq-qaaaq-cai";

function formatTime(ns: bigint): string {
	if (ns <= 0n) return "—";
	const ms = Number(ns / 1_000_000n);
	if (!Number.isFinite(ms)) return "—";
	return new Date(ms).toLocaleString();
}

/** Human labels for on-chain price sources only (play/production). */
function sourceLabel(
	src: string | null | undefined,
	opts?: { price?: number; status?: string },
): string {
	const price = opts?.price ?? 0;
	const status = opts?.status;
	if (src === "xrc") return "XRC (on-chain)";
	if (src === "external_aggregate") return "Multi-API fallback";
	if (src === "fallback_cache" || src === "last_good") return "Fallback cache";
	if (src === "admin_immediate" || src === "admin_manual") return "Admin";
	if (src === "market_dev") return "Local market average";
	if (src === "protocol" || src === "bootstrap") return "Protocol";
	if (src && src.length > 0 && src !== "fixture") return src;
	if (status === "missing" || price <= 0) return "Unavailable";
	// Live e8 with unset source after upgrade — still real chain data
	return "On-chain";
}

function statusBadge(
	status: string | undefined,
	stale: boolean,
): { label: string; className: string } {
	if (status === "market_fallback") {
		return {
			label: "Local only",
			className: "bg-warning/15 text-warning",
		};
	}
	if (status === "pending_osm") {
		return {
			label: "Pending delay",
			className: "bg-warning/15 text-warning",
		};
	}
	if (status === "stale" || stale) {
		return { label: "Stale", className: "bg-danger/15 text-danger" };
	}
	if (status === "missing") {
		return {
			label: "Unavailable",
			className: "bg-danger/15 text-danger",
		};
	}
	return { label: "Live", className: "bg-primary/15 text-primary" };
}

function feedStatusBanner(
	live: boolean,
	isError: boolean,
	isLoading: boolean,
): { label: string; detail: string; ok: boolean } {
	if (isLoading) {
		return {
			label: "Loading",
			detail: "Reading protocol price feeds…",
			ok: true,
		};
	}
	if (isError && !live) {
		return {
			label: "Unavailable",
			detail: "Could not load on-chain feeds. Check network and canister env.",
			ok: false,
		};
	}
	if (live) {
		return {
			label: "Live on-chain",
			detail:
				"Vault CR, mint limits, and liquidations use these protocol marks.",
			ok: true,
		};
	}
	return {
		label: "No marks yet",
		detail: "Protocol has not published collateral prices.",
		ok: false,
	};
}

/** Oracle Layer — autonomous on-chain marks + transparency. */
export default function OraclePage() {
	const {
		displayFeeds,
		source,
		providerQuotes,
		isLoading,
		isError,
		refetchProviders,
		fetchedAt,
		keeperUpdatedAt,
	} = useOracleTransparency();
	const production = isProductionOracle();
	const playOrLocal = isPlayOrLocalOracle();
	const local = isLocalOracle();
	const live = source === "live";
	const banner = feedStatusBanner(live, isError, isLoading);

	const [lastManualRefresh, setLastManualRefresh] = useState<number | null>(
		() => readLastManualRefreshAt(),
	);
	const [cooldownMs, setCooldownMs] = useState(0);

	useEffect(() => {
		const tick = () => {
			setCooldownMs(manualRefreshRemainingMs(lastManualRefresh));
		};
		tick();
		const id = window.setInterval(tick, 1000);
		return () => window.clearInterval(id);
	}, [lastManualRefresh]);

	const handleManualRefresh = () => {
		if (cooldownMs > 0) return;
		const now = Date.now();
		writeLastManualRefreshAt(now);
		setLastManualRefresh(now);
		void refetchProviders();
	};

	const feedsByAsset = new Map(displayFeeds.map((f) => [f.asset, f]));

	return (
		<div className="container mx-auto space-y-8 px-4 py-6 sm:py-8">
			<div>
				<p className="section-eyebrow">Oracle</p>
				<h1 className="text-xl font-bold tracking-tight sm:text-2xl">
					Collateral marks
				</h1>
				<p className="mt-1 max-w-2xl text-sm text-muted-foreground">
					On-chain USD marks for ICP, ckBTC, and ckETH. The protocol uses these
					for collateralization ratios, mint limits, and liquidations — not for
					PegSwap quotes.
				</p>
			</div>

			{/* Environment note — short, no fixture jargon */}
			{local ? (
				<div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm">
					<p className="font-semibold text-foreground">Local replica</p>
					<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
						Dev-only: if protocol feeds are missing, the UI may show browser
						market averages for display. Play and production never use browser
						prices for vault math.
					</p>
				</div>
			) : playOrLocal ? (
				<div className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-sm">
					<p className="font-semibold text-sky-950 dark:text-sky-100">
						Play Mode
					</p>
					<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
						Same autonomous oracle design as production: XRC on-chain timer +
						protocol pull. DEMO collateral tokens only — prices are real market
						marks via XRC.
					</p>
				</div>
			) : production ? (
				<div className="rounded-xl border border-border bg-muted/20 px-4 py-3 text-sm">
					<p className="font-semibold text-foreground">Production</p>
					<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
						Vault marks are protocol-only. Host multi-API keeper runs only if
						XRC is degraded.
					</p>
				</div>
			) : null}

			<div className="card-surface flex flex-wrap items-center gap-3 p-4">
				<span
					className={
						banner.ok
							? "rounded-full bg-primary/15 px-2.5 py-1 text-[10px] font-semibold uppercase text-primary"
							: "rounded-full bg-danger/15 px-2.5 py-1 text-[10px] font-semibold uppercase text-danger"
					}
				>
					{banner.label}
				</span>
				<span className="text-xs text-muted-foreground">{banner.detail}</span>
				<div className="ml-auto flex gap-2">
					<Link to={ROUTES.analytics}>
						<Button variant="secondary" size="sm">
							Analytics
						</Button>
					</Link>
				</div>
			</div>

			<AutonomyStatusPanel />

			{isLoading ? (
				<div className="grid gap-3 sm:grid-cols-3">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="skeleton-shimmer h-40" />
					))}
				</div>
			) : (
				<div className="grid gap-3 sm:grid-cols-3">
					{ASSETS.map((k) => {
						const f = feedsByAsset.get(k);
						const price = f?.price ?? 0;
						const badge = statusBadge(f?.status, f?.stale ?? true);
						return (
							<div
								key={k}
								className="card-surface flex flex-col items-center p-6 text-center"
							>
								<TokenIcon token={k} size="lg" plain />
								<p className="section-eyebrow mt-3">{k}</p>
								<p className="mt-2 font-mono-amount text-3xl font-bold">
									{price > 0 ? (
										<>
											$
											<AnimatedNumber
												value={price}
												decimals={k === "ICP" ? 2 : 0}
											/>
										</>
									) : (
										"—"
									)}
								</p>
								<p className="mt-2 text-[11px] text-muted-foreground">
									{sourceLabel(f?.oracleSource, {
										price,
										status: f?.status,
									})}{" "}
									· vault CR &amp; LTV
								</p>
								<span
									className={`mt-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.className}`}
								>
									{badge.label}
								</span>
							</div>
						);
					})}
				</div>
			)}

			{isError ? (
				<p className="text-center text-sm text-danger">
					Some oracle queries failed. Retry or check canister connectivity.
				</p>
			) : null}

			<section className="space-y-4">
				<div>
					<p className="section-eyebrow">Protocol feeds</p>
					<h2 className="text-lg font-bold">Marks used by the vault engine</h2>
					<p className="mt-1 max-w-2xl text-sm text-muted-foreground">
						Synced from the MotokoDollar oracle canister. Primary source is the
						DFINITY Exchange Rate Canister (
						<code className="text-xs">{XRC_MAINNET.slice(0, 12)}…</code>
						). Multi-API fallback only after XRC failure policy.
					</p>
				</div>
				<div className="space-y-2">
					{ASSETS.map((asset) => {
						const f = feedsByAsset.get(asset);
						const badge = statusBadge(f?.status, f?.stale ?? true);
						const price = f?.price ?? 0;
						return (
							<div
								key={asset}
								className={
									f?.stale || f?.status === "stale"
										? "card-surface flex flex-wrap items-center justify-between gap-3 border-warning/40 bg-warning/5 p-4"
										: "card-surface flex flex-wrap items-center justify-between gap-3 p-4"
								}
							>
								<div className="flex items-center gap-3">
									<TokenIcon token={asset} size="md" plain />
									<div>
										<p className="font-semibold">{asset}</p>
										<p className="text-xs text-muted-foreground">
											XRC market{" "}
											{asset === "ICP"
												? "ICP"
												: asset === "ckBTC"
													? "BTC"
													: "ETH"}
											/USD
										</p>
									</div>
								</div>
								<div className="text-right">
									<p className="font-mono-amount text-lg font-semibold">
										{price > 0
											? `$${formatNum(price, asset === "ICP" ? 2 : 0)}`
											: "—"}
									</p>
									<p className="text-xs text-muted-foreground">
										Updated {formatTime(f?.lastUpdated ?? 0n)}
									</p>
								</div>
								<div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
									<span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold">
										{sourceLabel(f?.oracleSource, {
											price,
											status: f?.status,
										})}
									</span>
									{f?.providerCount != null ? (
										<span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold">
											{f.providerCount} XRC sources
										</span>
									) : null}
									<span
										className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.className}`}
									>
										{badge.label}
									</span>
								</div>
							</div>
						);
					})}
				</div>
			</section>

			<section className="card-surface space-y-3 p-5">
				<p className="section-eyebrow">How the autonomous oracle works</p>
				<ol className="list-inside list-decimal space-y-2.5 text-sm text-muted-foreground">
					<li>
						<strong className="text-foreground">Oracle timer (on-chain)</strong>{" "}
						— the oracle canister refreshes ICP, ckBTC, and ckETH from XRC on a
						fixed interval (Play ~1h, production ~5m).
					</li>
					<li>
						<strong className="text-foreground">Protocol sync timer</strong> —
						the protocol pulls those marks into its price feeds (what you see
						above). Vault math never reads browser APIs.
					</li>
					<li>
						<strong className="text-foreground">If XRC fails</strong> — a host
						keeper can submit multi-provider consensus (fail-closed if providers
						disagree). A warm fallback cache may also apply if recently
						updated.
					</li>
					<li>
						<strong className="text-foreground">Staleness</strong> — outdated
						marks can block mint or liquidation until the next successful
						refresh.
					</li>
				</ol>
			</section>

			{/* Transparency only — not vault authority on play/production */}
			<ExternalProvidersSection
				providerQuotes={providerQuotes}
				keeperUpdatedAt={keeperUpdatedAt}
			/>

			<PriceCallHistoryPanel />

			<div className="flex flex-col items-center gap-2 pb-8 text-center">
				{fetchedAt != null ? (
					<p className="text-xs text-muted-foreground">
						Transparency quotes refreshed{" "}
						{new Date(fetchedAt).toLocaleTimeString()}
					</p>
				) : null}
				<p className="max-w-md text-xs text-muted-foreground">
					External provider table is transparency only. Auto-refresh ~30 min;
					manual refresh limited to once per 15 minutes.
				</p>
				<Button
					variant="secondary"
					size="sm"
					disabled={cooldownMs > 0}
					onClick={handleManualRefresh}
				>
					{cooldownMs > 0
						? `Refresh in ${formatCooldown(cooldownMs)}`
						: "Refresh transparency quotes"}
				</Button>
			</div>
		</div>
	);
}
